import { Menu, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/use-auth';
import { cn } from '@/lib/utils';

const defaultLinks = [
  { to: '/contact', label: 'Contact' },
];

const devotionalLinks = [
  { to: '/', label: 'Home' },
  { to: '/contact', label: 'Contact' },
];

export function PublicNav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { isAuthenticated, profile } = useAuth();
  const { pathname } = useLocation();
  const links = pathname === '/devotional' ? devotionalLinks : defaultLinks;
  const firstName = profile?.display_name?.split(' ')[0] ?? 'Back';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={cn(
        'sticky top-0 z-40 w-full transition-all duration-300',
        scrolled
          ? 'border-b border-ink/10 bg-background/80 backdrop-blur-md'
          : 'border-b border-transparent bg-transparent',
      )}
    >
      <div className="container-wide flex h-20 items-center justify-between">
        <Link to="/" className="group flex items-center gap-3">
          <Logomark className="h-8 w-8 text-primary transition-transform group-hover:rotate-3" />
          <span className="font-display text-lg leading-none tracking-tight">
            Gracefully
            <span className="block text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
              Rebuilt
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-10 md:flex">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                cn(
                  'relative text-sm font-medium text-foreground/70 transition-colors hover:text-foreground',
                  'after:absolute after:-bottom-1.5 after:left-0 after:h-[2px] after:w-0 after:bg-primary after:transition-all',
                  'hover:after:w-full',
                  isActive && 'text-foreground after:w-full',
                )
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          {isAuthenticated ? (
            <>
              <span className="text-sm text-muted-foreground">
                Hi, {firstName} 👋
              </span>
              <Button asChild size="sm">
                <Link to="/dashboard">Go to dashboard</Link>
              </Button>
            </>
          ) : (
            <>
              <Button asChild variant="ghost" size="sm">
                <Link to="/login">Sign in</Link>
              </Button>
              <Button asChild size="sm">
                <Link to="/join">Join the sisterhood</Link>
              </Button>
            </>
          )}
        </div>

        <button
          type="button"
          aria-label={open ? 'Close menu' : 'Open menu'}
          onClick={() => setOpen(!open)}
          className="md:hidden"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-ink/10 bg-background md:hidden">
          <nav className="container-wide flex flex-col gap-4 py-6">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="py-1 text-base font-medium"
              >
                {l.label}
              </Link>
            ))}
            <div className="mt-3 flex flex-col gap-2">
              {isAuthenticated ? (
                <Button asChild onClick={() => setOpen(false)}>
                  <Link to="/dashboard">
                    Go to dashboard
                  </Link>
                </Button>
              ) : (
                <>
                  <Button asChild variant="outline">
                    <Link to="/login" onClick={() => setOpen(false)}>
                      Sign in
                    </Link>
                  </Button>
                  <Button asChild>
                    <Link to="/join" onClick={() => setOpen(false)}>
                      Join the sisterhood
                    </Link>
                  </Button>
                </>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}

export function Logomark({ className }: { className?: string }) {
  return (
    <img
      src="/logo.png"
      alt="Gracefully Rebuilt"
      className={className}
    />
  );
}
